import React, { useState, useEffect, useRef } from 'react';
import { X, Save, Trash2, Camera, MapPin, Package, Tag, Info, Loader2 } from 'lucide-react';
import ColorSelector from './ColorSelector';
import PhotoUpload, { PhotoUploadRef } from './PhotoUpload';
import LocationDisplay from './LocationDisplay';
import LocationSelector from './LocationSelector';
import MovementHistory from './MovementHistory';
import Switch from './Switch';
import { generateSKU } from '../utils/skuGenerator';
import { photoPayloadForSave } from '../utils/photoStorage';
import { usePermissions } from '../hooks/usePermissions';
import { useCategoriesWithSubcategories } from '../hooks/useCategories';
import { useObjet } from '../hooks/useObjets';
import { useToastContext } from '../contexts/ToastContext';

const normalizeEtatForForm = (etat: string): string => {
  const map: Record<string, string> = {
    'à réparer': 'a_reparer',
    'à vérifier': 'a_verifier',
    hs: 'HS',
  };
  return map[etat] || etat;
};

const normalizePhotosForForm = (photos: any[] | undefined): string[] => {
  if (!photos?.length) return [];
  return photos
    .map((photo) => {
      if (typeof photo === 'string') return photo;
      if (photo?.s3Key) return `s3:${photo.s3Key}`;
      if (photo?.url) return photo.url;
      return '';
    })
    .filter(Boolean);
};

const mergePhotoUrls = (...sources: (any[] | undefined)[]): string[] => {
  const merged: string[] = [];
  for (const source of sources) {
    for (const photo of normalizePhotosForForm(source)) {
      if (!merged.includes(photo)) {
        merged.push(photo);
      }
    }
  }
  return merged;
};

interface ObjetEditModalProps {
  objet: any;
  isOpen: boolean;
  onClose: () => void;
  onSave: (
    updatedObjet: any,
    locationChanged: { hasChanged: boolean; originalZoneId?: string; originalBoxId?: string; originalPieceId?: string },
    photosToSave?: string[],
    saveOptions?: { photosModified?: boolean }
  ) => void | Promise<void>;
  onDelete?: (objetId: string) => void;
  initialSection?: 'infos' | 'localisation' | 'photos' | 'historique';
  inventoryMode?: boolean;
  isSaving?: boolean;
}

const ObjetEditModalMobile: React.FC<ObjetEditModalProps> = ({ 
  objet, 
  isOpen, 
  onClose, 
  onSave, 
  onDelete,
  initialSection = 'infos',
  inventoryMode = false,
  isSaving = false,
}) => {
  const permissions = usePermissions();
  const { showInfo } = useToastContext();
  const { data: categories = [], isLoading: categoriesLoading } = useCategoriesWithSubcategories();

  const [activeSection, setActiveSection] = useState<'infos' | 'localisation' | 'photos' | 'historique'>('infos');
  const [formData, setFormData] = useState({
    nom: '',
    categorie: '',
    sousCategorie: '',
    quantite: 1,
    etat: 'bon',
    besoinAchat: false,
    remarques: '',
    sku: '',
    couleurPrincipale: '',
    couleurSecondaire: '',
    zoneId: '',
    boxId: '',
    pieceId: '',
  });

  const [photos, setPhotos] = useState<string[]>([]);
  const [photosProcessing, setPhotosProcessing] = useState(false);
  const [itemId, setItemId] = useState<string | undefined>();
  const [originalLocation, setOriginalLocation] = useState<{ zoneId?: string; boxId?: string; pieceId?: string }>({});
  const photosRef = useRef<string[]>([]);
  const photoUploadRef = useRef<PhotoUploadRef>(null);
  const formInitKeyRef = useRef<string>('');
  const photosModifiedRef = useRef(false);

  const fetchObjetId = isOpen && objet?.id != null ? String(objet.id) : '';
  const { data: objetDetail } = useObjet(fetchObjetId);

  const updatePhotos = (value: React.SetStateAction<string[]>) => {
    setPhotos((prev) => {
      const next = typeof value === 'function' ? value(prev) : value;
      photosRef.current = next;
      return next;
    });
  };

  const handlePhotosChange: React.Dispatch<React.SetStateAction<string[]>> = (value) => {
    photosModifiedRef.current = true;
    updatePhotos(value);
  };

  useEffect(() => {
    if (!isOpen) {
      formInitKeyRef.current = '';
      photosModifiedRef.current = false;
      return;
    }

    const initKey = objet?.id != null
      ? `edit-${objet.id}`
      : objet
        ? `draft-${objet.nom || ''}-${objet.categorie || ''}`
        : 'new';

    if (formInitKeyRef.current === initKey) {
      return;
    }
    formInitKeyRef.current = initKey;

    if (objet) {
      setItemId(objet.id != null ? String(objet.id) : undefined);
      const initialData = {
        nom: objet.nom || '',
        categorie: objet.categorie || '',
        sousCategorie: objet.sousCategorie || '',
        quantite: objet.quantite || 1,
        etat: normalizeEtatForForm(objet.etat || 'bon'),
        besoinAchat: objet.besoinAchat || false,
        remarques: objet.remarques || '',
        sku: objet.sku || '',
        couleurPrincipale: objet.couleurPrincipale || '',
        couleurSecondaire: objet.couleurSecondaire || '',
        zoneId: objet.zoneId || '',
        boxId: objet.boxId || '',
        pieceId: objet.pieceId || '',
      };

      setFormData(initialData);
      updatePhotos(normalizePhotosForForm(objet.photos));
      setOriginalLocation({ zoneId: objet.zoneId, boxId: objet.boxId, pieceId: objet.pieceId });
    } else {
      setItemId(undefined);
      setFormData({
        nom: '',
        categorie: '',
        sousCategorie: '',
        quantite: 1,
        etat: 'bon',
        besoinAchat: false,
        remarques: '',
        sku: '',
        couleurPrincipale: '',
        couleurSecondaire: '',
        zoneId: '',
        boxId: '',
        pieceId: '',
      });
      updatePhotos([]);
      setOriginalLocation({});
    }
  }, [isOpen, objet]);

  // La liste inventaire est en mode « lite » (URLs vides) — charger les photos résolues depuis l'API
  useEffect(() => {
    if (!isOpen || !objet?.id || !objetDetail?.photos?.length) {
      return;
    }
    const resolved = normalizePhotosForForm(objetDetail.photos);
    if (resolved.length > 0) {
      updatePhotos(resolved.map((photo) => photoPayloadForSave(photo)));
    }
  }, [isOpen, objet?.id, objetDetail?.id, objetDetail?.photos]);

  useEffect(() => {
    if (!isOpen) return;

    const hasPhotos =
      (objet?.photoCount ?? 0) > 0 || normalizePhotosForForm(objet?.photos).length > 0;
    const hasLocation = !!(objet?.zoneId || objet?.boxId || objet?.pieceId);

    if (inventoryMode) {
      if (!hasPhotos) {
        setActiveSection('photos');
      } else if (!hasLocation) {
        setActiveSection('localisation');
      } else {
        setActiveSection(initialSection === 'infos' ? 'localisation' : initialSection);
      }
    } else {
      setActiveSection(initialSection);
    }
  }, [isOpen, initialSection, inventoryMode, objet?.id, objet?.photos, objet?.zoneId, objet?.boxId, objet?.pieceId]);

  useEffect(() => {
    if (!formData.nom?.trim() || !formData.categorie?.trim()) return;
    if (itemId && formData.sku?.trim()) return;

    const generatedSKU = generateSKU({
      categorie: formData.categorie,
      sousCategorie: formData.sousCategorie,
      nom: formData.nom,
      zoneId: formData.zoneId,
      boxId: formData.boxId,
      pieceId: formData.pieceId,
    });

    setFormData((prev) => (prev.sku === generatedSKU ? prev : { ...prev, sku: generatedSKU }));
  }, [
    formData.nom,
    formData.categorie,
    formData.sousCategorie,
    formData.zoneId,
    formData.boxId,
    formData.pieceId,
    formData.sku,
    itemId,
  ]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (isSaving) return;

    if (photoUploadRef.current?.hasPendingUploads()) {
      showInfo(
        'Photos en cours',
        'Finalisation de la compression et de l\'envoi avant enregistrement…',
        8000,
      );
      await photoUploadRef.current.waitForUploads();
    }

    const hasChangedLocation =
      formData.zoneId !== originalLocation.zoneId ||
      formData.boxId !== originalLocation.boxId ||
      formData.pieceId !== originalLocation.pieceId;

    let sku = formData.sku?.trim();
    if (!sku && formData.nom?.trim() && formData.categorie?.trim()) {
      sku = generateSKU({
        categorie: formData.categorie,
        sousCategorie: formData.sousCategorie,
        nom: formData.nom,
        zoneId: formData.zoneId,
        boxId: formData.boxId,
        pieceId: formData.pieceId,
      });
    }

    const photosToSave = mergePhotoUrls(photosRef.current, photos, objet?.photos).map((entry) =>
      photoPayloadForSave(entry),
    );

    await onSave(
      {
        id: itemId,
        ...(objet || {}),
        ...formData,
        sku,
        photos: photosToSave,
      },
      {
        hasChanged: hasChangedLocation,
        originalZoneId: originalLocation.zoneId,
        originalBoxId: originalLocation.boxId,
        originalPieceId: originalLocation.pieceId,
      },
      photosToSave,
      { photosModified: photosModifiedRef.current }
    );
  };

  const handleDelete = () => {
    if (window.confirm(`Êtes-vous sûr de vouloir supprimer "${objet.nom}" ?`)) {
      onDelete?.(objet.id);
      onClose();
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    
    if (name === 'categorie') {
      setFormData(prev => ({ 
        ...prev, 
        [name]: value,
        sousCategorie: ''
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
      }));
    }
  };

  const handleColorChange = (field: 'couleurPrincipale' | 'couleurSecondaire', value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleGenerateSKU = () => {
    if (formData.nom && formData.categorie) {
      const generatedSKU = generateSKU({
        categorie: formData.categorie,
        sousCategorie: formData.sousCategorie,
        nom: formData.nom,
        zoneId: formData.zoneId,
        boxId: formData.boxId,
        pieceId: formData.pieceId,
      });
      setFormData(prev => ({ ...prev, sku: generatedSKU }));
    }
  };

  if (!isOpen) return null;

  const sections = [
    { id: 'infos' as const, label: 'Infos', icon: Info },
    { id: 'localisation' as const, label: 'Lieu', icon: MapPin },
    { id: 'photos' as const, label: 'Photos', icon: Camera },
    { id: 'historique' as const, label: 'Déplacements', icon: Package },
  ];

  return (
    <div className="fixed inset-0 z-50 overflow-hidden bg-black bg-opacity-50 flex items-end sm:items-center sm:justify-center">
      {/* Modal Container - Mobile first avec slide-up animation */}
      <div className="w-full h-full sm:h-auto sm:max-h-[90vh] sm:max-w-2xl bg-white sm:rounded-t-2xl sm:rounded-b-2xl flex flex-col animate-slide-up relative">
        {isSaving && (
          <div className="absolute inset-0 z-20 bg-white/80 backdrop-blur-[1px] flex flex-col items-center justify-center gap-3 rounded-t-2xl">
            <Loader2 className="h-10 w-10 text-blue-600 animate-spin" />
            <p className="text-sm font-medium text-gray-700">Enregistrement en cours…</p>
          </div>
        )}
        
        {/* Header - Sticky */}
        <div className="flex-shrink-0 sticky top-0 z-10 bg-white border-b border-gray-200 px-4 py-3 sm:px-6 sm:py-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg sm:text-xl font-bold text-gray-900 truncate flex-1 pr-2">
              {permissions.canEdit ? 'Modifier l\'objet' : 'Détails de l\'objet'}
            </h2>
            <button
              onClick={onClose}
              className="flex-shrink-0 p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
            >
              <X className="h-5 w-5 sm:h-6 sm:w-6" />
            </button>
          </div>

          {inventoryMode && (
            <p className="mt-2 text-xs sm:text-sm text-blue-800 bg-blue-50 border border-blue-100 rounded-md px-3 py-2">
              Inventaire : ajoutez une photo, vérifiez l&apos;état et indiquez où se trouve l&apos;objet aujourd&apos;hui.
            </p>
          )}

          {/* Navigation par onglets - Mobile optimisé */}
          <div className="mt-3 flex space-x-1 overflow-x-auto scrollbar-hide">
            {sections.map((section) => {
              const Icon = section.icon;
              return (
                <button
                  key={section.id}
                  onClick={() => setActiveSection(section.id)}
                  className={`flex items-center space-x-1.5 px-3 sm:px-4 py-2 rounded-lg whitespace-nowrap transition-all ${
                    activeSection === section.id
                      ? 'bg-blue-600 text-white shadow-md'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  <span className="text-sm font-medium">{section.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Content - Scrollable */}
        <div className="flex-1 overflow-y-auto px-4 py-4 sm:px-6">
          <form onSubmit={handleSubmit} id="objet-form">
            
            {/* Section Informations */}
            {activeSection === 'infos' && (
              <div className="space-y-4">
                {/* Nom - Input mobile-optimisé */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Nom de l'objet *
                  </label>
                  <input
                    type="text"
                    name="nom"
                    value={formData.nom}
                    onChange={handleChange}
                    disabled={!permissions.canEdit}
                    required
                    className="w-full px-4 py-3 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                    placeholder="Ex: Ballon de football"
                  />
                </div>

                {/* Catégorie - Select mobile-optimisé */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Catégorie *
                  </label>
                  <select
                    name="categorie"
                    value={formData.categorie}
                    onChange={handleChange}
                    disabled={!permissions.canEdit}
                    required
                    className="w-full px-4 py-3 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  >
                    <option key="empty-categorie" value="">
                      {categoriesLoading ? 'Chargement...' : 'Sélectionner une catégorie'}
                    </option>
                    {categories.map((category) => (
                      <option key={category.id} value={category.nom}>{category.nom}</option>
                    ))}
                  </select>
                </div>

                {formData.categorie && (() => {
                  const selectedCategory = categories.find((cat) => cat.nom === formData.categorie);
                  return selectedCategory?.sousCategories && selectedCategory.sousCategories.length > 0 ? (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">
                        Sous-catégorie
                      </label>
                      <select
                        name="sousCategorie"
                        value={formData.sousCategorie}
                        onChange={handleChange}
                        disabled={!permissions.canEdit}
                        className="w-full px-4 py-3 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                      >
                        <option key="empty-souscategorie" value="">Sélectionner une sous-catégorie</option>
                        {selectedCategory.sousCategories.map((subCategory) => (
                          <option key={subCategory.id} value={subCategory.nom}>{subCategory.nom}</option>
                        ))}
                      </select>
                    </div>
                  ) : null;
                })()}

                {/* Quantité et État - Grid responsive */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Quantité *
                    </label>
                    <input
                      type="number"
                      name="quantite"
                      value={formData.quantite}
                      onChange={handleChange}
                      disabled={!permissions.canEdit}
                      min="0"
                      required
                      className="w-full px-4 py-3 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      État *
                    </label>
                    <select
                      name="etat"
                      value={formData.etat}
                      onChange={handleChange}
                      disabled={!permissions.canEdit}
                      required
                      className="w-full px-4 py-3 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                    >
                      <option key="bon" value="bon">Bon</option>
                      <option key="a_reparer" value="a_reparer">À réparer</option>
                      <option key="incomplet" value="incomplet">Incomplet</option>
                      <option key="manquant" value="manquant">Manquant</option>
                      <option key="HS" value="HS">Hors Service</option>
                      <option key="a_verifier" value="a_verifier">À vérifier</option>
                    </select>
                  </div>
                </div>

                {/* Besoin d'achat - Switch moderne et tactile */}
                <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <Switch
                    checked={formData.besoinAchat}
                    onChange={(checked) => setFormData(prev => ({ ...prev, besoinAchat: checked }))}
                    label="Besoin d'achat"
                    disabled={!permissions.canEdit}
                    size="lg"
                    color="orange"
                  />
                  <p className="text-xs text-gray-500 mt-2 ml-1">
                    {formData.besoinAchat 
                      ? '🛒 Cet objet doit être acheté' 
                      : 'Pas de besoin d\'achat actuellement'}
                  </p>
                </div>

                {/* SKU - Avec bouton de génération */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    SKU (Code unique)
                  </label>
                  <div className="flex space-x-2">
                    <input
                      type="text"
                      name="sku"
                      value={formData.sku}
                      onChange={handleChange}
                      disabled={!permissions.canEdit}
                      className="flex-1 px-4 py-3 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                      placeholder="Généré automatiquement"
                    />
                    {permissions.canEdit && (
                      <button
                        type="button"
                        onClick={handleGenerateSKU}
                        className="px-4 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors whitespace-nowrap"
                      >
                        <Tag className="h-5 w-5" />
                      </button>
                    )}
                  </div>
                </div>

                {/* Couleurs - Selecteurs tactiles */}
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Couleur principale
                    </label>
                    <ColorSelector
                      value={formData.couleurPrincipale}
                      onChange={(color) => handleColorChange('couleurPrincipale', color)}
                      disabled={!permissions.canEdit}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Couleur secondaire
                    </label>
                    <ColorSelector
                      value={formData.couleurSecondaire}
                      onChange={(color) => handleColorChange('couleurSecondaire', color)}
                      disabled={!permissions.canEdit}
                    />
                  </div>
                </div>

                {/* Remarques - Textarea mobile-optimisé */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Remarques
                  </label>
                  <textarea
                    name="remarques"
                    value={formData.remarques}
                    onChange={handleChange}
                    disabled={!permissions.canEdit}
                    rows={4}
                    className="w-full px-4 py-3 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none disabled:bg-gray-100 disabled:cursor-not-allowed"
                    placeholder="Ajoutez des remarques..."
                  />
                </div>
              </div>
            )}

            {/* Section Localisation */}
            {activeSection === 'localisation' && (
              <div className="space-y-4">
                <LocationSelector
                  currentZoneId={formData.zoneId}
                  currentBoxId={formData.boxId}
                  currentPieceId={formData.pieceId}
                  onLocationChange={(zoneId, boxId, pieceId) => {
                    setFormData(prev => ({
                      ...prev,
                      zoneId: zoneId || '',
                      boxId: boxId || '',
                      pieceId: pieceId || '',
                    }));
                  }}
                  disabled={!permissions.canMove}
                  showCurrentLocation={true}
                />

                {(formData.zoneId !== originalLocation.zoneId ||
                  formData.boxId !== originalLocation.boxId ||
                  formData.pieceId !== originalLocation.pieceId) && permissions.canMove && (
                  <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <p className="text-sm text-yellow-800 font-medium flex items-center space-x-2">
                      <MapPin className="h-4 w-4" />
                      <span>Changement de localisation détecté</span>
                    </p>
                    <p className="text-xs text-yellow-700 mt-1">
                      Ce déplacement sera enregistré dans l'historique lors de l'enregistrement.
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Section Photos — toujours montée pour ne pas perdre une compression en cours */}
            <div className={activeSection === 'photos' ? 'space-y-4' : 'hidden'}>
              <PhotoUpload
                ref={photoUploadRef}
                photos={photos}
                onPhotosChange={handlePhotosChange}
                onProcessingChange={setPhotosProcessing}
                itemId={objet?.id != null ? String(objet.id) : undefined}
                objetNom={formData.nom || objet?.nom}
              />
            </div>

            {/* Section Historique */}
            {activeSection === 'historique' && (
              <div>
                <MovementHistory itemId={objet.id} compact />
              </div>
            )}
          </form>
        </div>

        {/* Footer - Sticky avec actions */}
        <div className="flex-shrink-0 sticky bottom-0 bg-white border-t border-gray-200 px-4 py-3 sm:px-6 space-y-2">
          {/* Boutons d'action */}
          <div className="flex space-x-2">
            {permissions.canEdit && (
              <button
                type="submit"
                form="objet-form"
                disabled={isSaving}
                className="flex-1 flex items-center justify-center space-x-2 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 active:bg-blue-800 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSaving ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <Save className="h-5 w-5" />
                )}
                <span>
                  {isSaving
                    ? 'Enregistrement…'
                    : photosProcessing
                      ? 'Finalisation photos…'
                      : 'Enregistrer'}
                </span>
              </button>
            )}

            {permissions.canDelete && onDelete && (
              <button
                type="button"
                onClick={handleDelete}
                className="flex items-center justify-center space-x-2 px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 active:bg-red-800 transition-colors font-medium"
              >
                <Trash2 className="h-5 w-5" />
                <span className="sm:inline hidden">Supprimer</span>
              </button>
            )}

            <button
              type="button"
              onClick={onClose}
              disabled={isSaving}
              className="px-4 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 active:bg-gray-400 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Annuler
            </button>
          </div>

          {/* Indicateur de modification de localisation */}
          {(formData.zoneId !== originalLocation.zoneId ||
            formData.boxId !== originalLocation.boxId ||
            formData.pieceId !== originalLocation.pieceId) && (
            <p className="text-xs text-center text-gray-600">
              💾 Ce déplacement sera enregistré dans l'historique
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default ObjetEditModalMobile;

